import React from 'react';
import classNames from 'classnames';
import { BASE_IMG_URL } from '@images/imageUrls';
import FeedbackProblemProductPickers, {
    type FeedbackProblemProductPickersHandle,
} from '@components/feedbackProblemProductPickers';
import type { FeedbackProductCategory, FeedbackProductItem, FetchProductsParams } from '@components/feedbackProductPicker';
import type { FeedbackProblemCategory, FeedbackProblemItem } from '@components/feedbackProblemPicker';
import type { FeedbackFilterOption, FeedbackFilterParams } from '../index';
import styles from './index.module.scss';
import { releaseModalBodyScrollLock, syncModalBodyScrollLock } from '@utils/modalScrollLock';

/**
 * H5 端筛选区（与 PC 不同）：
 * - 列表页仅展示类型 / 机型 / 筛选栏；搜索在独立搜索页
 * - 搜索页传入 showSearchBar，展示搜索栏并自动聚焦
 */
export interface H5FeedbackFilterContentProps {
    filterParams: FeedbackFilterParams;
    searchInputValue: string;
    /** 搜索页模式：展示顶部搜索栏 */
    showSearchBar?: boolean;
    solveStateList: FeedbackFilterOption[];
    problemList: FeedbackFilterOption[];
    problemCategories: FeedbackProblemCategory[];
    productList: FeedbackFilterOption[];
    productCategories: FeedbackProductCategory[];
    daysList: FeedbackFilterOption[];
    onSearchChange?: (value: string) => void;
    onSearchConfirm?: () => void;
    onSearchBack?: () => void;
    onFilterChange?: (key: keyof FeedbackFilterParams, value: string) => void;
    onAdvancedFilterConfirm?: (values: Pick<FeedbackFilterParams, 'solveState' | 'daysValue'>) => void;
    onProblemSelect?: (problem: FeedbackProblemItem | null) => void;
    onProductSelect?: (product: FeedbackProductItem | null) => void;
    fetchProducts?: (params: FetchProductsParams) => Promise<FeedbackProductItem[]>;
}

interface H5FeedbackFilterContentState {
    /** H5「筛选」弹窗：状态 + 时间 */
    advancedFilterVisible: boolean;
    /** 高级筛选草稿值，点确定后才写入 filterParams */
    draftSolveState: string;
    draftDaysValue: string;
}

class H5FeedbackFilterContent extends React.Component<H5FeedbackFilterContentProps, H5FeedbackFilterContentState> {
    searchInputRef = React.createRef<HTMLInputElement>();
    pickerRef = React.createRef<FeedbackProblemProductPickersHandle>();

    state: H5FeedbackFilterContentState = {
        advancedFilterVisible: false,
        draftSolveState: '',
        draftDaysValue: '',
    };

    componentDidMount() {
        if (this.props.showSearchBar) {
            this.searchInputRef.current?.focus();
        }
    }

    componentDidUpdate(prevProps: H5FeedbackFilterContentProps, prevState: H5FeedbackFilterContentState) {
        if (this.props.showSearchBar && !prevProps.showSearchBar) {
            this.searchInputRef.current?.focus();
        }
        syncModalBodyScrollLock(this.state.advancedFilterVisible, prevState.advancedFilterVisible);
    }

    componentWillUnmount() {
        releaseModalBodyScrollLock(this.state.advancedFilterVisible);
    }

    getSelectedLabel = (list: FeedbackFilterOption[], value: string, fallback: string) => {
        if (!value) return fallback;
        const selected = list.find((item) => item.value === value);
        return selected?.name || fallback;
    };

    handleSearchBack = () => {
        this.props.onSearchBack?.();
    };

    handleSearchClear = () => {
        this.props.onSearchChange?.('');
        this.searchInputRef.current?.focus();
    };

    handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            this.props.onSearchConfirm?.();
        }
    };

    openAdvancedFilter = () => {
        const { filterParams } = this.props;
        this.setState({
            advancedFilterVisible: true,
            draftSolveState: filterParams.solveState,
            draftDaysValue: filterParams.daysValue,
        });
    };

    handleAdvancedConfirm = () => {
        const { draftSolveState, draftDaysValue } = this.state;
        this.setState({ advancedFilterVisible: false });
        this.props.onAdvancedFilterConfirm?.({
            solveState: draftSolveState,
            daysValue: draftDaysValue,
        });
    };

    renderPillGroup = (
        label: string,
        key: 'draftSolveState' | 'draftDaysValue',
        options: FeedbackFilterOption[],
    ) => {
        const value = this.state[key];

        return (
            <div className={styles.advancedSection}>
                <div className={styles.advancedLabel}>{label}</div>
                <div className={styles.advancedTags}>
                    {options.map((item) => (
                        <span
                            key={`${key}-${item.id}`}
                            className={classNames(
                                styles.advancedTag,
                                value === item.value && styles.advancedTagActive,
                            )}
                            onClick={() => this.setState({ [key]: item.value } as Pick<H5FeedbackFilterContentState, typeof key>)}
                        >
                            {item.name}
                        </span>
                    ))}
                </div>
            </div>
        );
    };

    render() {
        const {
            filterParams,
            searchInputValue,
            showSearchBar = false,
            solveStateList,
            problemList,
            problemCategories,
            productList,
            productCategories,
            daysList,
            onSearchChange,
            onProblemSelect,
            onProductSelect,
            fetchProducts,
            onSearchConfirm,
        } = this.props;

        const {
            advancedFilterVisible,
        } = this.state;

        const problemLabel = this.getSelectedLabel(problemList, filterParams.problemId, '类型');
        const productLabel = this.getSelectedLabel(productList, filterParams.productId, '机型');
        const canSearch = !!searchInputValue.trim();
        const hasAdvancedFilter = !!(filterParams.solveState || filterParams.daysValue);

        return (
            <>
                {showSearchBar && (
                    <div className={styles.filterHeader}>
                        <div className={styles.backBtn} onClick={this.handleSearchBack}>
                            <span className={styles.backArrow} />
                        </div>

                        <div className={classNames(styles.searchBox, styles.searchBoxActive)}>
                            <img
                                src={`${BASE_IMG_URL}/common/search.png`}
                                alt="search"
                                className={styles.searchIcon}
                            />
                            <input
                                ref={this.searchInputRef}
                                type="text"
                                className={styles.searchInput}
                                placeholder="搜索问题"
                                value={searchInputValue}
                                onChange={(e) => onSearchChange?.(e.target.value)}
                                onKeyDown={this.handleSearchKeyDown}
                            />
                            {!!searchInputValue && (
                                <button
                                    type="button"
                                    className={styles.clearBtn}
                                    onClick={this.handleSearchClear}
                                    aria-label="清空"
                                />
                            )}
                        </div>

                        <button
                            type="button"
                            className={classNames(styles.searchBtn, canSearch && styles.searchBtnActive)}
                            onClick={() => onSearchConfirm?.()}
                            disabled={!canSearch}
                        >
                            搜索
                        </button>
                    </div>
                )}

                <div className={styles.filterBar}>
                    <div
                        className={classNames(styles.filterItem, filterParams.problemId && styles.filterItemActive)}
                        onClick={() => this.pickerRef.current?.openProblemPicker()}
                    >
                        <span className={styles.filterItemText}>{problemLabel}</span>
                        <img
                            src={`${BASE_IMG_URL}/common/arrow_b2.png`}
                            alt=""
                            className={styles.filterArrow}
                        />
                    </div>

                    <div
                        className={classNames(styles.filterItem, filterParams.productId && styles.filterItemActive)}
                        onClick={() => this.pickerRef.current?.openProductPicker()}
                    >
                        <span className={styles.filterItemText}>{productLabel}</span>
                        <img
                            src={`${BASE_IMG_URL}/common/arrow_b2.png`}
                            alt=""
                            className={styles.filterArrow}
                        />
                    </div>

                    <div
                        className={classNames(styles.filterAction, hasAdvancedFilter && styles.filterActionActive)}
                        onClick={this.openAdvancedFilter}
                    >
                        <span className={styles.filterActionText}>筛选</span>
                        <span className={styles.filterIcon} />
                    </div>
                </div>

                {advancedFilterVisible && (
                    <div className={styles.advancedMask} onClick={() => this.setState({ advancedFilterVisible: false })}>
                        <div className={styles.advancedSheet} onClick={(e) => e.stopPropagation()} data-body-scroll-lock-scrollable>
                            <div className={styles.advancedTitle}>筛选</div>
                            {this.renderPillGroup('状态', 'draftSolveState', solveStateList)}
                            {this.renderPillGroup('时间', 'draftDaysValue', daysList)}
                            <div className={styles.advancedFooter}>
                                <button
                                    type="button"
                                    className={styles.advancedCancelBtn}
                                    onClick={() => this.setState({ advancedFilterVisible: false })}
                                >
                                    取消
                                </button>
                                <button
                                    type="button"
                                    className={styles.advancedConfirmBtn}
                                    onClick={this.handleAdvancedConfirm}
                                >
                                    确定
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <FeedbackProblemProductPickers
                    ref={this.pickerRef}
                    problemCategories={problemCategories}
                    productCategories={productCategories}
                    problemId={filterParams.problemId}
                    productId={filterParams.productId}
                    fetchProducts={fetchProducts}
                    onProblemSelect={onProblemSelect}
                    onProductSelect={onProductSelect}
                />
            </>
        );
    }
}

export default H5FeedbackFilterContent;
